import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { SelectField } from '@app/components/form-fields/SelectField';
import { TextAreaField } from '@app/components/form-fields/TextAreaField';
import { TextField } from '@app/components/form-fields/TextField';
import useAgentTypes from '@app/hooks/agents/useAgentTypes';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import type { UseFormReset } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { MdCheck } from 'react-icons/md';
import * as z from 'zod';

const schema = z.object({
    name: z.string().trim().min(1, 'Name is required'),
    type_id: z.string('Type is required'),
    prompt: z.string().trim().min(1, 'Prompt is required'),
});

export type AgentFormValues = z.infer<typeof schema>;

export type AgentFormProps = {
    id: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any;
    loading?: boolean;
    onCancel?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    onSubmit: (data: AgentFormValues, reset: UseFormReset<AgentFormValues>) => void;
};

export const AgentForm = ({ onSubmit, onCancel, data, ...props }: AgentFormProps) => {
    const {
        handleSubmit,
        control,
        reset,
        formState: { isValid, isSubmitting },
    } = useForm<AgentFormValues>({
        mode: 'all',
        resolver: zodResolver(schema),
        defaultValues: {
            name: '',
            type_id: undefined,
            prompt: '',
        },
    });

    const { data: agentTypes } = useAgentTypes();
    const agentTypeOptions =
        agentTypes?.map((item) => ({
            label: item.name,
            value: item.id,
        })) || [];

    useEffect(() => {
        reset(data);
    }, [data, reset]);

    return (
        <form onSubmit={handleSubmit((values) => onSubmit(values, reset))} id={props.id}>
            <div className="form-fields-wrapper">
                <div className="grid">
                    <div className="grid-xs-12">
                        <Controller
                            control={control}
                            name="name"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Name"
                                    type="text"
                                    placeholder="Enter name"
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="type_id"
                            render={({ field, fieldState }) => (
                                <SelectField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Type"
                                    placeholder="Select type"
                                    options={agentTypeOptions}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="prompt"
                            render={({ field, fieldState }) => (
                                <TextAreaField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Prompt"
                                    placeholder="Enter prompt"
                                />
                            )}
                        />
                    </div>
                </div>
            </div>

            <div className="ant-modal-footer">
                <StyledButtonGroup justify="flex-end">
                    <StyledButton onClick={onCancel} color="black" data-testid="reset-button">
                        Cancel
                    </StyledButton>
                    <StyledButton
                        htmlType="submit"
                        type="primary"
                        disabled={!isValid}
                        loading={isSubmitting || props?.loading}
                        icon={<MdCheck />}
                        data-testid="add-agent-button">
                        Save
                    </StyledButton>
                </StyledButtonGroup>
            </div>
        </form>
    );
};
